import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMain } from '../../hooks/useMain';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const TEMPLATE_HEADERS = [
  'consumeableProductName',
  'productVolume',
  'productUnit',
  'consumeableProductCount',
];

const getField = (row, key) => {
  if (row[key] !== undefined) return row[key];
  const rowKeys = Object.keys(row);
  const match = rowKeys.find((k) => norm(k) === norm(key));
  return match !== undefined ? row[match] : undefined;
};

// duplicate WITHIN the file = same product name + volume + unit
const duplicateKey = (r) => `${norm(r.consumeableProductName)}|${norm(r.productVolume)}|${norm(r.productUnit)}`;

/**
 * existingProducts: pass in `allConsumeproducts` from the parent so we can
 * flag rows that match a product already saved for this branch (by name).
 */
const BulkUploadConsumeProductDialog = ({ onUploaded, existingProducts = [] }) => {
  const { activeBranchId, createManyConsumeableProduct, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const fileInputRef = useRef(null);

  const existingNameSet = useMemo(
    () => new Set(existingProducts.map((p) => norm(p.consumeableProductName))),
    [existingProducts]
  );

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const downloadExcelTemplate = () => {
    const sampleData = [
      {
        consumeableProductName: 'Hair Oil',
        productVolume: 200,
        productUnit: 'ml',
        consumeableProductCount: 20,
      },
      {
        consumeableProductName: 'Hair Color',
        productVolume: 100,
        productUnit: 'g',
        consumeableProductCount: 15,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ConsumeableProducts');
    XLSX.writeFile(wb, 'consumeable_product_template.xlsx');
  };

  const buildFlags = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.invalid) return;
      const key = duplicateKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.map((r) => ({
      ...r,
      isDuplicateInFile: !r.invalid && counts.get(duplicateKey(r)) > 1,
      alreadyExists: !r.invalid && existingNameSet.has(norm(r.consumeableProductName)),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!jsonData.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        let mapped = jsonData.map((item, idx) => {
          const consumeableProductName = (getField(item, 'consumeableProductName') || '').toString().trim();
          const productVolumeRaw = getField(item, 'productVolume');
          const productVolume = productVolumeRaw === '' ? '' : Number(productVolumeRaw);
          const productUnit = (getField(item, 'productUnit') || '').toString().trim();
          const countRaw = getField(item, 'consumeableProductCount');
          const consumeableProductCount = countRaw === '' ? '' : Number(countRaw);

          const row = {
            rowIndex: idx + 2,
            consumeableProductName,
            productVolume: isNaN(productVolume) ? '' : productVolume,
            productUnit,
            consumeableProductCount: isNaN(consumeableProductCount) ? '' : consumeableProductCount,
          };

          const missingFields = [];
          if (!row.consumeableProductName) missingFields.push('consumeableProductName');
          if (row.productVolume === '') missingFields.push('productVolume');
          if (!row.productUnit) missingFields.push('productUnit');
          if (row.consumeableProductCount === '') missingFields.push('consumeableProductCount');

          row.invalid = missingFields.length > 0;
          row.invalidReason = missingFields.length ? `Missing/invalid: ${missingFields.join(', ')}` : '';
          return row;
        });

        mapped = buildFlags(mapped);
        setParsedRows(mapped);

        // by default select valid rows EXCEPT already-existing ones,
        // so the user has to opt-in to updating an existing product
        setSelectedRows(
          mapped.filter((r) => !r.invalid && !r.alreadyExists).map((r) => r.rowIndex)
        );
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .xls file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveRow = (rowIndex) => {
    setParsedRows((prev) => buildFlags(prev.filter((r) => r.rowIndex !== rowIndex)));
    setSelectedRows((prev) => prev.filter((i) => i !== rowIndex));
  };

  const handleRemoveAllFileDuplicates = () => {
    const seen = new Set();
    const deduped = [];
    parsedRows.forEach((r) => {
      if (r.invalid) {
        deduped.push(r);
        return;
      }
      const key = duplicateKey(r);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(r);
    });
    const removedCount = parsedRows.length - deduped.length;
    setParsedRows(buildFlags(deduped));
    setSelectedRows((prev) => prev.filter((i) => deduped.some((r) => r.rowIndex === i)));
    setShowMessage?.({
      message: removedCount > 0 ? `Removed ${removedCount} duplicate row(s)` : 'No duplicates found in file',
      messageType: removedCount > 0 ? 'success' : 'error',
    });
  };

  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : selectableRows.map((r) => r.rowIndex));
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const fileDuplicateCount = parsedRows.filter((r) => r.isDuplicateInFile).length;
  const alreadyExistsCount = parsedRows.filter((r) => r.alreadyExists).length;
  const readyCount = selectableRows.length;

  const handleSubmit = async () => {
    if (parsedRows.length === 0) {
      setShowMessage({ message: 'Upload a valid file', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({ message: 'Select at least one row to upload', messageType: 'error' });
      return;
    }

    const rowsToUpload = parsedRows
      .filter((r) => selectedRows.includes(r.rowIndex))
      .map((r) => ({
        consumeableProductName: r.consumeableProductName,
        productVolume: r.productVolume,
        productUnit: r.productUnit,
        consumeableProductCount: r.consumeableProductCount,
      }));

    setSubmitting(true);
    try {
      const res = await createManyConsumeableProduct(activeBranchId, { bulkData: rowsToUpload });
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Uploaded successfully', messageType: 'success' });
        handleClose();
        onUploaded?.();
      } else {
        setShowMessage({ message: res?.message || 'Upload failed', messageType: 'error' });
      }
    } catch (err) {
      setShowMessage({ message: err.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<MdCloudUpload />}
        sx={{ background: '#6174DD', height: '40px', minWidth: '200px' }}
        onClick={handleOpen}
      >
        Upload Consumeable Product
      </Button>

      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        maxWidth={parsedRows.length > 0 ? 'lg' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          Bulk Upload Consumable Products{' '}
          <Tooltip
            title="Upload an excel file with columns: consumeableProductName, productVolume, productUnit, consumeableProductCount. Rows whose product name already exists are flagged — uploading them will UPDATE that product's existing details. Uncheck any row you don't want touched."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={downloadExcelTemplate}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {alreadyExistsCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {alreadyExistsCount} product(s) in this file already exist (matched by name). They are unchecked
              by default — if you leave them checked and upload, their existing details will be
              <strong> updated/overwritten</strong> with the values from this file. Uncheck any you don't want to touch.
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${readyCount} valid`} color="success" />
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" />}
                {fileDuplicateCount > 0 && <Chip label={`${fileDuplicateCount} duplicate rows in file`} color="warning" />}
                {alreadyExistsCount > 0 && <Chip label={`${alreadyExistsCount} already present`} color="info" />}
                <Chip label={`${selectedRows.length} selected`} color="primary" variant="outlined" />
                {fileDuplicateCount > 0 && (
                  <Button size="small" color="warning" variant="outlined" onClick={handleRemoveAllFileDuplicates}>
                    Remove file duplicates (keep first)
                  </Button>
                )}
              </Box>

              <Box sx={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Volume</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Count</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid
                            ? '#ffebee'
                            : r.alreadyExists
                            ? '#e3f2fd'
                            : r.isDuplicateInFile
                            ? '#fff3e0'
                            : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.consumeableProductName || '—'}</TableCell>
                        <TableCell>{r.productVolume === '' ? '—' : r.productVolume}</TableCell>
                        <TableCell>{r.productUnit || '—'}</TableCell>
                        <TableCell>{r.consumeableProductCount === '' ? '—' : r.consumeableProductCount}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Tooltip title={r.invalidReason || 'Invalid row'}>
                              <Chip size="small" label="Invalid" color="error" />
                            </Tooltip>
                          ) : r.alreadyExists ? (
                            <Chip size="small" label="Already exists" color="info" />
                          ) : r.isDuplicateInFile ? (
                            <Chip size="small" label="Duplicate in file" color="warning" />
                          ) : (
                            <Chip size="small" label="New" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={r.invalid ? 'error' : 'text.secondary'}>
                            {r.invalid
                              ? r.invalidReason
                              : r.alreadyExists
                              ? 'Product name matches an existing one — will update if checked'
                              : r.isDuplicateInFile
                              ? 'Same product+volume+unit as another row'
                              : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveRow(r.rowIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Selected Product(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadConsumeProductDialog;